import { FEE_PRODUCT_TYPE, SERVICE_PRODUCT_TYPE } from "constants/sales";
import type { LineItem, Sale } from "services/types/sales.type";

import {
  PA_SERVICE_FIELD_TAGS,
  PA_SERVICE_SLOT_COUNT,
  PA_V_CODE_SERVICE_FIELD_TAGS,
  paServiceTag,
  paVCodeServiceTag
} from "../merge-tags-options/purchase-agreement-tags.utils";
import { getCatalogName } from "./common/configurable-merge-tags";
import { excludeReturnedOrExchanged, getSortedItems } from "./common/utils";
import type { MergeTagsReplaceHandler } from "./merge-tags-values";
import { LINE_VALUES } from "./purchase-agreement-tag-values.utils";

/*
 * ENG-2503 values: each service or dispensing fee by its place in the invoice table (Service 1 is the first
 * the table prints), and Anvil's "V-code service", the first service whose own CPT code starts with "V"
 * (suno-backend billing/views/purchase_agreement.py, get_v_service_data). Money and CPT code read the line
 * the way the hearing-aid tags do (purchase-agreement-tag-values.utils.ts).
 */

const isServiceOrFee = (item: LineItem) => {
  const type = item.product?.master_product?.type;
  return type === SERVICE_PRODUCT_TYPE || type === FEE_PRODUCT_TYPE;
};

export const getServiceLines = (sale: Sale) =>
  getSortedItems(excludeReturnedOrExchanged(sale.items)).filter(isServiceOrFee);

const getVCodeService = (sale: Sale) =>
  excludeReturnedOrExchanged(sale.items).find(
    (item) =>
      item.product?.master_product?.type === SERVICE_PRODUCT_TYPE &&
      item.cpt_code?.code?.startsWith("V")
  );

export const SERVICE_LINE_VALUES: Record<string, (item: LineItem) => string> = {
  ...LINE_VALUES,
  Name: (item) => getCatalogName(item),
  Description: (item) => item.description ?? ""
};

type Handler = MergeTagsReplaceHandler[string];

const forLine =
  (pick: (sale: Sale) => LineItem | undefined, field: string): Handler =>
  ({ data }) => {
    const item = data.contextSale ? pick(data.contextSale) : undefined;
    return item ? SERVICE_LINE_VALUES[field](item) : "";
  };

export const PURCHASE_AGREEMENT_SERVICE_VALUES: MergeTagsReplaceHandler =
  Object.fromEntries([
    ...Array.from({ length: PA_SERVICE_SLOT_COUNT }, (_, index) =>
      PA_SERVICE_FIELD_TAGS.map((field) => [
        paServiceTag(index + 1, field),
        forLine((sale) => getServiceLines(sale)[index], field)
      ])
    ).flat(),
    ...PA_V_CODE_SERVICE_FIELD_TAGS.map((field) => [
      paVCodeServiceTag(field),
      forLine(getVCodeService, field)
    ])
  ]);
